/* eslint-disable react/no-danger */
import {
  AutocompleteMenuSelector,
  ChainOption,
  FormGroup,
  TransWithDefault,
} from '@starsoft/common/components';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { useNotificationService } from '@starsoft/common/hooks/useNotificationService';
import { Chain } from '@starsoft/common/models';
import { ChainIDType } from '@starsoft/common/models/chain/id';
import {
  NotificationsService,
  SettingsService,
} from '@starsoft/common/services';
import { QRCodeSVG } from 'qrcode.react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import TransactionsCommonCopyAddress from '../../../../../../../../components/Transactions/Common/CopyAddress';
import { useTranslation } from '../../../../../../../../hooks';
import { useDepositForm } from '../../../../../../../../hooks/deposit';
import useFormattedCryptoAddress from '../../../../../../../../hooks/useFormattedCryptoAddress';
import DepositCoinSelect from '../../Layout/CoinSelect';
import styles from './styles.module.scss';

/**
 * TransactionsModalsDepositContentFormCryptoContent Component
 * This component is responsible for rendering the crypto deposit form content.
 * It includes functionalities for selecting a coin, displaying a QR code for the deposit address,
 * and copying the deposit address to the clipboard.
 *
 * @returns {JSX.Element} The rendered component displaying the crypto deposit form content.
 *
 * @example
 * // Usage example:
 * <TransactionsModalsDepositContentFormCryptoContent />
 */
export default function TransactionsModalsDepositContentFormCryptoContent() {
  const { t } = useTranslation('deposit');
  const { watch, setValue } = useFormContext<CreateDepositRequestDto>();
  const NotificationsService: NotificationsService<unknown> =
    useNotificationService();
  const {
    chainOptions,
    accountByCoinQuery: {
      currentAccount,
      accountAddress,
      isLoadingAccountByCoin,
    },
    isFormLoading,
  } = useDepositForm(watch, setValue);

  const isLoading: boolean = isFormLoading || isLoadingAccountByCoin;

  const chainId: bigint | undefined = watch('crypto.chainId');
  const chain: Chain | undefined = useMemo(
    () =>
      chainOptions?.find(
        chain => chain?.id?.toString() === chainId?.toString(),
      ),
    [chainId, chainOptions],
  );
  const formattedAddress = useFormattedCryptoAddress(accountAddress?.address);

  /**
   * Handles the selection of a blockchain network.
   * Updates the form value for the selected chain ID.
   *
   * @param {Chain} chain - The selected chain object.
   *
   * @example
   * handleSelectChain({ id: '1', name: 'Ethereum', symbol: 'ETH' });
   */
  function handleSelectChain(chain: Chain) {
    setValue('crypto.chainId', chain?.id as ChainIDType, {
      shouldDirty: true,
    });
  }

  /**
   * Handles the copying of the deposit address to the clipboard.
   * Displays a success notification upon successful copy.
   *
   * @example
   * handleCopyAddress();
   */
  function handleCopyAddress() {
    NotificationsService.success({
      title: 'copy_base_title',
      message: 'copy_deposit_address',
    });

    if (typeof navigator != 'undefined') {
      navigator?.clipboard?.writeText(String(accountAddress?.address));
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__wrapper}>
        <DepositCoinSelect />
        <FormGroup
          label={t('crypto_form_network_label')}
          isLoading={isLoading}
          loading={{ label: true, secondary: false }}
        >
          <AutocompleteMenuSelector
            hideSearch
            title={t('crypto_form_network_label_select')}
            options={chainOptions || []}
            filterBy={['name', 'symbol']}
            loading={isLoading}
            value={chainId}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id}
            setValue={handleSelectChain}
            isContainerColor
            customComponents={{
              customOption: ({ handleSelect, option, selected }) => (
                <ChainOption
                  option={option}
                  selected={selected}
                  handleSelect={handleSelect}
                />
              ),
            }}
          />
        </FormGroup>
      </div>
      <div className={styles.container__qr__container}>
        <div className={styles.container__qr__container__column}>
          <FormGroup
            label={t('crypto_form_deposit_address_label')}
            isLoading={isLoading}
            loading={{ label: true, secondary: false }}
          >
            <TransactionsCommonCopyAddress
              isLoading={isLoading}
              onCopy={handleCopyAddress}
              address={formattedAddress}
            />
          </FormGroup>
        </div>
        <div className={styles.container__qr}>
          {isLoading ? (
            <div className={styles.container__qr__skeleton} />
          ) : (
            <QRCodeSVG
              value={accountAddress?.address ?? ''}
              size={130}
              imageSettings={{
                height: 32,
                width: 32,
                src: `${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${currentAccount?.coin?.image}`,
                x: 50,
                y: 50,
                excavate: true,
              }}
            />
          )}
        </div>
      </div>
      {(!isLoading && (
        <div className={styles.container__card}>
          <i className="fa-solid fa-circle-exclamation" />
          <TransWithDefault
            i18nKey="deposit:crypto_form_deposit_warning"
            values={{
              currency: currentAccount?.coin?.code,
              confirmations: chain?.confirmations,
            }}
          />
        </div>
      )) || (
        <div className={styles.container__card}>
          <div className={styles.icon} />
          <div className={styles.text__container}>
            <div className={styles.text} />
            <div className={styles.text} />
            <div className={styles.text} />
            <div className={styles.text} />
          </div>
        </div>
      )}
    </div>
  );
}
